import type { BIRevision } from '@/shared/generated'

import { Navigate } from '@tanstack/react-router'
import { prop } from 'lodash/fp'
import { ChartPieIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { CurrencyPicker } from '@/features/currency-picker'

import { useBIRevisionQuery } from '@/entities/bi'
import { CurrencyScopeProvider, useCurrenciesQuery } from '@/entities/currency'
import { Access } from '@/entities/session'

import DelayedMount from '@/shared/components/DelayedMount'
import DocumentTitle from '@/shared/components/DocumentTitle'
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbPage,
} from '@/shared/components/ui/breadcrumb'
import { Content, Header } from '@/shared/components/ui/layout'
import { Loader } from '@/shared/components/ui/loader'
import { Separator } from '@/shared/components/ui/separator'

import MainDashboardBusinessSection from './MainDashboardBusinessSection'
import MainDashboardMetricsSection from './MainDashboardMetricsSection'
import MainDashboardOperationsSection from './MainDashboardOperationsSection'
import MainDashboardPricingHistoricalSection from './MainDashboardPricingHistoricalSection'

export default function MainDashboard() {
	return (
		<CurrencyScopeProvider>
			<MainDashboardContent />
		</CurrencyScopeProvider>
	)
}

const MainDashboardContent = () => {
	const { t } = useTranslation()

	const biRevisionDateQuery = useBIRevisionQuery<string | null>({
		refetchOnMount: 'always',
		select: prop('date') as (data: BIRevision | null) => string | null,
	})

	// settle currencies first so sections mount once with the final currency (no double fetch)
	const currenciesQuery = useCurrenciesQuery()

	return (
		<Access permissions={['ACCESS_BI']} fallback={<Navigate to='/p/og' />}>
			<DocumentTitle title={t('main_dashboard_title')}>
				<Header className='h-11 items-center justify-between border-b px-4 py-2'>
					<div className='flex items-center'>
						<ChartPieIcon className='size-4' />
						<Separator orientation='vertical' className='mx-2 h-5' />
						<Breadcrumb className='text-sm'>
							<BreadcrumbList>
								<BreadcrumbItem>
									<BreadcrumbPage>{t('main_dashboard_title')}</BreadcrumbPage>
								</BreadcrumbItem>
							</BreadcrumbList>
						</Breadcrumb>
					</div>
					<CurrencyPicker />
				</Header>
				<Content className='overflow-hidden' id='dashboard-container'>
					<div className='size-full overflow-hidden'>
						<div
							className='relative flex h-full flex-col gap-4 overflow-y-scroll'
							id='dashboard-scrollable-area'
						>
							{currenciesQuery.isPending ? (
								<div className='flex h-full items-center justify-center'>
									<Loader />
								</div>
							) : (
								<>
									<MainDashboardOperationsSection />
									{!biRevisionDateQuery.isLoading &&
										biRevisionDateQuery.data && (
											<>
												<Access permissions={['ACCESS_BI']}>
													<MainDashboardPricingHistoricalSection
														biRevisionDate={biRevisionDateQuery.data}
													/>
												</Access>
												<Access permissions={['ACCESS_BI']}>
													<DelayedMount delay={500} fallback={<div />}>
														<MainDashboardBusinessSection
															biRevisionDate={biRevisionDateQuery.data}
														/>
													</DelayedMount>
												</Access>
												<Access permissions={['ACCESS_BI']}>
													<DelayedMount delay={1000} fallback={<div />}>
														<MainDashboardMetricsSection
															biRevisionDate={biRevisionDateQuery.data}
														/>
													</DelayedMount>
												</Access>
											</>
										)}
								</>
							)}
						</div>
					</div>
				</Content>
			</DocumentTitle>
		</Access>
	)
}
